import { FC, useContext, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../../components/ui/Header";
import { FlexContent, InlineOrderEditor, TableCell, TableHeadCell, TableRow, useDebounce } from "@rothoblaas/design-system";
import Table from "../../components/ui/Table";
import { getEditAction } from "../../utils/rowActions";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import useInitializer from "../../hooks/useInitializer";
import useListFilters from "../../hooks/useListFilters";
import useI18n from "../../hooks/useTranslations";
import { SearchCategory } from "../../http/types/categories";
import { useCategoriesApi } from "../../http/useCategories";
import { PaginationQuery } from "../../types";

const AdminCategories: FC = () => {
  const i18n = useI18n();
  const filters = useListFilters({ isSearchable: true });
  const aborter = useRef(new AbortController());
  const categoriesApi = useCategoriesApi();
  const dispatch = useContext(NotificationDispatch);
  const navigate = useNavigate();

  const updateOrder = async (code: string, newOrder: number) => {
    try {
      await categoriesApi.updateAdminCategoryOrder(code, { order: newOrder });
      dispatch(showSuccessNotification(i18n.translation));
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const debouncedValue = useDebounce(filters.state.q);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { data, error } = categoriesApi.useAdminCategories(query, aborter.current.signal);

  const isInitialized = useInitializer(data || error);

  if (!data && !error && !isInitialized) {
    return;
  }

  return (
    <>
      <Header title={i18n.translation.categories.plural} path={{ display: i18n.translation.categories.all }} />

      <FlexContent padding noPaddingTop>
        <Table<SearchCategory>
          filters={filters}
          search={{
            placeholder: i18n.translation.categories.search,
            onChange: () => {
              aborter.current.abort();
              aborter.current = new AbortController();
            },
          }}
          head={
            <TableRow>
              <TableHeadCell align="left">{i18n.translation.common.name}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.common.description}</TableHeadCell>
              <TableHeadCell align="center">{i18n.translation.common.order}</TableHeadCell>
              <TableHeadCell></TableHeadCell>
            </TableRow>
          }
          items={data?.items}
          renderRow={(Category) => (
            <>
              <TableCell>
                <Link onClick={(e) => e.stopPropagation()} to={Category.code}>
                  {Category.name}
                </Link>
              </TableCell>
              <TableCell>{Category.description}</TableCell>
              <TableCell align="center">
                <InlineOrderEditor order={Category.order} onUpdate={(newOrder) => updateOrder(Category.code, newOrder)} />
              </TableCell>
            </>
          )}
          rowActions={(Category) => [getEditAction(i18n, () => navigate(`/admin/categories/${Category.code}`))]}
          onRowClick={(Category) => navigate(`/admin/categories/${Category.code}`)}
          total={data?.total}
          error={!!error}
        />
      </FlexContent>
    </>
  );
};

export default AdminCategories;
